import React, { useState } from 'react';
import ColorPickerField from '../../components/ColorPickerField';
import {
    Container,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Paper,
    Typography,
    Box,
    Divider,
    Switch,
    FormGroup,
} from '@mui/material';
import {
    Style,
    TextFields,
    Wifi,
    Link,
    Email,
    Visibility,
    CropFree,
    Circle,
    Square,
    Favorite
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';

const DesignerCanvas = () => {
    const [contentType, setContentType] = useState('url');
    const [qrContent, setQrContent] = useState('');
    const [foreground, setForeground] = useState('#000000');
    const [background, setBackground] = useState('#ffffff');
    const [scanPosition, setScanPosition] = useState('bottom');
    const [frameEnabled, setFrameEnabled] = useState(false);
    const [qrShape, setQrShape] = useState('square');
    const [eyeFrameShape, setEyeFrameShape] = useState('square');
    const [eyeBallShape, setEyeBallShape] = useState('square');
    const [wifiDetails, setWifiDetails] = useState({
        ssid: '',
        password: '',
        encryption: 'WPA'
    });

    const generateQRValue = () => {
        switch (contentType) {
            case 'wifi':
                return `WIFI:S:${wifiDetails.ssid};T:${wifiDetails.encryption};P:${wifiDetails.password};;`;
            case 'email':
                return `mailto:${qrContent}`;
            case 'url':
                return qrContent.startsWith('http') ? qrContent : `https://${qrContent}`;
            default:
                return qrContent;
        }
    };

    const handleWifiChange = (field, value) => {
        setWifiDetails(prev => ({ ...prev, [field]: value }));
    };

    const getTextPosition = () => {
        if (!frameEnabled) return scanPosition;

        const positions = {
            top: { top: 8, left: '50%', transform: 'translateX(-50%)' },
            bottom: { bottom: 8, left: '50%', transform: 'translateX(-50%)' },
            left: { left: 8, top: '50%', transform: 'translateY(-50%)' },
            right: { right: 8, top: '50%', transform: 'translateY(-50%)' }
        };

        return positions[scanPosition];
    };

    const getEyeRadius = () => {
        const shapeMap = {
            square: 0,
            rounded: 8,
            circle: 50
        };

        return [
            { // Top-left eye
                outer: shapeMap[eyeFrameShape],
                inner: shapeMap[eyeBallShape]
            },
            { // Top-right eye
                outer: shapeMap[eyeFrameShape],
                inner: shapeMap[eyeBallShape]
            },
            { // Bottom-left eye
                outer: shapeMap[eyeFrameShape],
                inner: shapeMap[eyeBallShape]
            }
        ];
    };

    const getQRComponents = () => {
        const shapeOptions = {
            square: {
                nodeType: 'rect',
                nodeSize: 1,
                nodeRadius: 0
            },
            rounded: {
                nodeType: 'rect',
                nodeSize: 1,
                nodeRadius: 8
            },
            circle: {
                nodeType: 'circle',
                nodeSize: 0.8,
                nodeRadius: 50
            }
        };

        return {
            components: {
                node: {
                    type: shapeOptions[qrShape].nodeType,
                    size: shapeOptions[qrShape].nodeSize,
                    radius: shapeOptions[qrShape].nodeRadius
                }
            }
        };
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    QR Code Generator
                </Typography>

                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel><Style sx={{ mr: 1 }} />Content Type</InputLabel>
                            <Select
                                value={contentType}
                                onChange={(e) => setContentType(e.target.value)}
                            >
                                <MenuItem value="url"><Link sx={{ mr: 1 }} />URL</MenuItem>
                                <MenuItem value="text"><TextFields sx={{ mr: 1 }} />Text</MenuItem>
                                <MenuItem value="email"><Email sx={{ mr: 1 }} />Email</MenuItem>
                                <MenuItem value="wifi"><Wifi sx={{ mr: 1 }} />WiFi</MenuItem>
                            </Select>
                        </FormControl>

                        {contentType === 'wifi' ? (
                            <>
                                <TextField
                                    fullWidth
                                    label="SSID"
                                    value={wifiDetails.ssid}
                                    onChange={(e) => handleWifiChange('ssid', e.target.value)}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Password"
                                    value={wifiDetails.password}
                                    onChange={(e) => handleWifiChange('password', e.target.value)}
                                    sx={{ mb: 2 }}
                                />
                                <FormControl fullWidth>
                                    <InputLabel>Encryption</InputLabel>
                                    <Select
                                        value={wifiDetails.encryption}
                                        onChange={(e) => handleWifiChange('encryption', e.target.value)}
                                    >
                                        <MenuItem value="WPA">WPA/WPA2</MenuItem>
                                        <MenuItem value="WEP">WEP</MenuItem>
                                        <MenuItem value="nopass">None</MenuItem>
                                    </Select>
                                </FormControl>
                            </>
                        ) : (
                            <TextField
                                fullWidth
                                label={`Enter ${contentType}`}
                                multiline
                                rows={4}
                                value={qrContent}
                                onChange={(e) => setQrContent(e.target.value)}
                                variant="outlined"
                            />
                        )}

                        <Divider sx={{ my: 4 }} />

                        <Typography variant="h6" gutterBottom>
                            <Favorite sx={{ mr: 1 }} />Customization
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                            <ColorPickerField label="Foreground" value={foreground} onChange={(e) => setForeground(e.target.value)} />

                            <ColorPickerField label="Background" value={background} onChange={(e) => setBackground(e.target.value)} />
                        </Box>

                        <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
                            <Typography gutterBottom>Scan Text Position</Typography>
                            <RadioGroup
                                row
                                value={scanPosition}
                                onChange={(e) => setScanPosition(e.target.value)}
                            >
                                <FormControlLabel value="top" control={<Radio />} label="Top" />
                                <FormControlLabel value="bottom" control={<Radio />} label="Bottom" />
                                <FormControlLabel value="left" control={<Radio />} label="Left" />
                                <FormControlLabel value="right" control={<Radio />} label="Right" />
                            </RadioGroup>
                        </FormControl>

                        <FormGroup sx={{ mb: 3 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={frameEnabled}
                                        onChange={(e) => setFrameEnabled(e.target.checked)}
                                    />
                                }
                                label="Enable Text Frame"
                            />
                        </FormGroup>

                        <Divider sx={{ my: 4 }} />

                        <Typography variant="h6" gutterBottom>
                            <Visibility sx={{ mr: 1 }} />Shape Settings
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel><CropFree /> QR Body</InputLabel>
                                    <Select
                                        value={qrShape}
                                        onChange={(e) => setQrShape(e.target.value)}
                                    >
                                        <MenuItem value="square">Squares</MenuItem>
                                        <MenuItem value="rounded">Rounded</MenuItem>
                                        <MenuItem value="circle">Circles</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel><Square /> Eye Frame</InputLabel>
                                    <Select
                                        value={eyeFrameShape}
                                        onChange={(e) => setEyeFrameShape(e.target.value)}
                                    >
                                        <MenuItem value="square">Square</MenuItem>
                                        <MenuItem value="rounded">Rounded</MenuItem>
                                        <MenuItem value="circle">Circle</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth sx={{ mb: 3 }}>
                                    <InputLabel><Circle /> Eye Ball</InputLabel>
                                    <Select
                                        value={eyeBallShape}
                                        onChange={(e) => setEyeBallShape(e.target.value)}
                                    >
                                        <MenuItem value="square">Square</MenuItem>
                                        <MenuItem value="rounded">Rounded</MenuItem>
                                        <MenuItem value="circle">Circle</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{
                            position: 'relative',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px'
                        }}>
                            <Box sx={{
                                position: 'relative',
                                padding: frameEnabled ? '20px' : 0,
                                border: frameEnabled ? `2px solid ${foreground}` : 'none',
                                borderRadius: 1
                            }}>
                                <QRCodeSVG
                                    value={generateQRValue()}
                                    fgColor={foreground}
                                    bgColor={background}
                                    size={256}
                                    level="H"
                                    includeMargin={true}
                                    eyeRadius={getEyeRadius()}
                                    {...getQRComponents()}
                                />

                                {frameEnabled && (
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            position: 'absolute',
                                            ...getTextPosition(),
                                            color: foreground,
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold',
                                            textShadow: `1px 1px 2px ${background}`,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        SCAN ME
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
};

export default DesignerCanvas;